import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CreateAccountCheckout from "../../sections/create-account-form-checkout";
import HeroAccount from "../../sections/hero-account";
import MembershipInfoBlock from "../../sections/member-info-block";
import BodyText from "../../elements/body-text";
import { getCountryList, getEncryptor, isNetworkError } from "../../services/siteService";
import { getOrderDetail } from "../../services/orderService";
import { setUserInfoAction } from "../../redux/actions";
import { S_AUTH_TOKEN, webStorage } from "../../utils/webStorage";
import useQuery from "../../utils/useQuery";
import api from "../../utils/api";
import VerifyEmailForm from "../../sections/verify-email-form";
import InfoPanel from "../../sections/info-panel";
import { bindCreateAccountConsents, getUserInfo } from "../../services/userService";
import { codeTypes } from "../../utils/lynkco-constants";
import { createLeads } from "../../services/leadsService";
import getSiteInfo from "../../utils/getSiteInfo";
import { staffInfoSelector } from "../../selectors/application";
import { getEmailCode } from "../../services/verificationService";
import "./style.scss";

const CheckoutCreateAccount = ({ rendering, fields, lang }) => {
  const {
    backButton,
    body,
    termsAndConditions,
    checkboxTermsOfUseLabel,
    createAccountFailedMessage,
    accountExistsMessage,
    phoneExixtsMessage,
    formTitle,
    formFields,
    heroBackgroundImage,
    heroHeadingBody,
    heroHeadingType,
    infoBody,
    infoHeadingBody,
    infoHeadingType,
    infoText,
    infoTitle,
    submitButton,
    loginSuccessRedirectUrl,
    verifyEmailForm,
    infoPanelStep2,
    newsletter,
    newsletterCheckboxBody,
    newsletterCheckboxLabel,
    loginNetworkErrorMessage,
    registrationInProcessMessage
  } = fields;
  const { id: newsletterConsentNo } = newsletter;

  const setFormFile = arr => {
    let formObj = {};
    arr.forEach(item => {
      let itemFields = item?.fields;
      switch (itemFields.name.value.toLowerCase()) {
        case "createpassword":
          formObj.password = {
            label: itemFields.label.value,
            type: "password",
            warningMessage: itemFields.warningMessage.value,
            infoMessage: itemFields.infoMessage.value
          };
          break;
        default:
          formObj[itemFields.name.value] = {
            label: itemFields.label.value,
            type: "",
            warningMessage: itemFields.warningMessage.value,
            infoMessage: itemFields.infoMessage.value
          };
      }
    });
    return formObj;
  };
  const [accountExists, setAccountExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);
  const [registrationInProcess, setRegistrationInProcess] = useState(false);
  const [sendCodeFailed, setSendCodeFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isInVerificationStep, setIsVerificationStep] = useState(false);
  const [isNetworkFailed, setIsNetworkFailed] = useState(false);
  const [createAccountFailed, setCreateAccountFailed] = useState(false);
  // const [loginFailed, setLoginFailed] = useState(null);
  // const [country, setCountry] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [orderDetail, setOrderDetail] = useState(null);
  const [reachedMax, setReachedMax] = useState();
  const [submitFailed, setSubmitFailed] = useState(false);
  const [formData, setFormData] = useState(null);
  const query = useQuery();
  const history = useHistory();
  const dispatch = useDispatch();
  const setUserInfo = data => dispatch(setUserInfoAction(data));
  const staffInfo = useSelector(staffInfoSelector);
  useEffect(() => {
    init();
  }, []);
  const init = async () => {
    const regions = await getCountryList();
    const orderId = query.orderId || "";
    try {
      const orderDetailRes = await getOrderDetail(orderId);
      setOrderDetail(orderDetailRes);
    } catch (e) {
      setCreateAccountFailed(true);
    }
    setCountryList(
      regions.map(e => ({
        value: e.regionName,
        code: e.regionId + "",
        abbr: e.regionAbbr
      }))
    );
  };
  const onAccountCreate = async (evt, formData) => {
    setSubmitting(true);
    setIsNetworkFailed(false);
    setAccountExists(false);
    setPhoneExists(false);
    setRegistrationInProcess(false);
    if (orderDetail?.billingInfo) {
      let data = orderDetail.billingInfo;
      let countryValue = countryList.find(e => e.abbr === data.countryCode);
      const encryptor = await getEncryptor(data.email);
      const encryptedPassword = encryptor.encrypt(formData.password);
      data = {
        ...data,
        ...formData,
        countryCode: countryValue?.code
      };
      setFormData(data);
      try {
        await api.account.register.check(null, {
          email: data.email,
          mobileCountryCode: data.mobileCountryCode,
          mobileNo: data.mobile,
          password: encryptedPassword
        });
      } catch (ex) {
        switch (ex.code) {
          case "USER_CUSTOMER_0008":
            setAccountExists(true);
            break;
          case "USER_CUSTOMER_026":
            setPhoneExists(true);
            break;
          case "USER_CUSTOMER_0031":
            setRegistrationInProcess(true);
            break;
          default:
            setCreateAccountFailed(true);
            break;
        }
        setSubmitting(false);
        return;
      }
      try {
        await getEmailCode(data.email, codeTypes.activeAccount);
        setIsVerificationStep(true);
      } catch (ex) {
        if (ex.code === "USER_CUSTOMER_0013") {
          setReachedMax(true);
        } else {
          setSendCodeFailed(true);
        }
      }
      setSubmitting(false);
    }
  };
  const bind = async data => {
    return await bindCreateAccountConsents({
      email: data.email,
      countryCode: data.countryCode,
      termsAndConditionNos: termsAndConditions.map(t => t.id),
      newsletterConsentNo,
      wantsNewsletter: data.subscribed,
      agreedToTerms: data.agreedToTerms
    });
  };

  const login = async () => {
    // setLoginFailed(false);
    const encryptor = await getEncryptor(formData.email);
    try {
      const { obj: token } = await api.auth.token(null, {
        username: formData.email,
        password: encryptor.encrypt(formData.password)
      });
      webStorage.set(S_AUTH_TOKEN, {
        ...token,
        loginTime: new Date().getTime()
      });
      const user = await getUserInfo();
      setUserInfo(user);
      await bindOrder(user);
      if (query.redirect) {
        if (query.redirect.startsWith("https://") || query.redirect.startsWith("http://")) {
          global.location.href = `${query.redirect}?token=${token.accessToken}`;
        } else {
          history.replace(query.redirect);
        }
      } else if (loginSuccessRedirectUrl.value) {
        global.location.href = loginSuccessRedirectUrl.value.href;
      }
    } catch (ex) {
      if (isNetworkError(ex)) {
        setIsNetworkFailed(true);
      } else {
        setIsNetworkFailed(false);
        // setLoginFailed(true);
      }
    }
  };

  const bindOrder = async data => {
    const orderNo = query.orderNo || "";
    const userAccount = data?.userAccount;
    await api.order.change(null, {
      orderNo: orderNo,
      userAccount: userAccount
    });
  };

  const onVerificationCodeSubmitted = async (evt, data) => {
      let contractId;
      try {
        contractId = await bind(formData);
        const siteInfo = await getSiteInfo();
        const encryptor = await getEncryptor(formData.email);
        const encryptedPassword = encryptor.encrypt(formData.password);
        const body = {
          verify: {
            email: formData.email,
            verificationCode: data.code,
            type: codeTypes.activeAccount
          },
          account: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            countryId: formData.countryCode,
            // birthday: format(formData.birthday, siteInfo.dateFormat),
            password: encryptedPassword,
            mobileCountryCode: formData.mobileCountryCode,
            mobileNo: formData.mobile,
            contractId: contractId,
            subscribed: formData.subscribed ? "1" : "0",
            dateFormat: siteInfo.dateFormat,
            email: formData.email
          }
        };
        if (staffInfo?.account?.userName) {
          body.account.staff = staffInfo?.account?.userName;
        }
        await api.account.register.byEmail(null, body);
      } catch (ex) {
        console.log("ex", ex);
        if (isNetworkError(ex)) {
          setIsNetworkFailed(true);
        } else {
          switch (ex.code) {
            case "USER_CUSTOMER_0008":
              setAccountExists(true);
              setIsVerificationStep(false);
              break;
            case "USER_CUSTOMER_0011":
              setSubmitFailed(true);
              break;
            case "USER_CUSTOMER_026":
              setPhoneExists(true);
              setIsVerificationStep(false);
              break;
            case "USER_CUSTOMER_0031":
              setRegistrationInProcess(true);
              break;
            default:
              setCreateAccountFailed(true);
              break;
          }
        }
        return;
      }
      await login();
      if (contractId && formData.subscribed) {
        await createLeads(lang, formData.email, formData.countryCode, contractId);
      }
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    },
    //onResendVerificationCode = (evt, data) =>

    hero = {
      heroHeadingBody,
      heroHeadingType,
      backgroundImage: heroBackgroundImage
    },
    info = {
      infoTitle,
      infoHeadingBody,
      infoHeadingType,
      infoBody
    },
    accountInfo = {
      createAccountForm: {
        ...setFormFile(formFields),
        heading: formTitle.value,
        email: orderDetail?.billingInfo?.email,
        firstName: orderDetail?.billingInfo?.firstName,
        lastName: orderDetail?.billingInfo?.lastName,
        phonenumber: `${orderDetail?.billingInfo?.mobileCountryCode} ${orderDetail?.billingInfo?.mobile}`,
        checkboxTermsOfUse: {
          label: checkboxTermsOfUseLabel.value
        },
        checkboxNewsletter: {
          label: newsletterCheckboxLabel.value,
          infoText: newsletterCheckboxBody.value
        },
        infoText: infoText.value,
        submitButton: {
          buttonText: submitButton?.fields.link.value.text,
          iconName: submitButton?.fields.iconName
        },
        backButton: { text: backButton?.fields.link.value.text },
        createAccountFailedMessage: createAccountFailedMessage,
        accountExistsMessage: accountExistsMessage,
        phoneExixtsMessage,
        isNetworkFailed,
        loginNetworkErrorMessage
      }
    };
  return (
    <div className="checkout-create-account" datasourceid={rendering.dataSource}>
      {!isInVerificationStep && (
        <div className={"grid"}>
          <HeroAccount {...hero} />
          <BodyText html={body.value} className="checkout-create-account-body " />
          <CreateAccountCheckout
            {...{
              ...accountInfo.createAccountForm,
              createAccountFailed,
              accountExists,
              registrationInProcess,
              phoneExists,
              submitting,
              submitCreateAccount: (evt, data) => onAccountCreate(evt, data)
            }}
          />
          <MembershipInfoBlock {...info} />
        </div>
      )}
      {isInVerificationStep && (
        <div className={"grid"}>
          <HeroAccount {...hero} />
          <VerifyEmailForm
            {...{
              ...verifyEmailForm.fields,
              sendCodeFailed,
              submittedEmail: formData.email,
              isReachedMax: reachedMax,
              codeType: codeTypes.activeAccount,
              lang,
              onVerificationCodeSubmitted,
              submitFailed,
              createAccountFailed,
              isNetworkFailed,
              registrationInProcess,
              createAccountFailedMessage,
              loginNetworkErrorMessage,
              registrationInProcessMessage
            }}
          />
          {infoPanelStep2 && <InfoPanel {...infoPanelStep2.fields} />}
        </div>
      )}
    </div>
  );
};

CheckoutCreateAccount.propTypes = {
  hero: PropTypes.object,
  accountInfo: PropTypes.object,
  info: PropTypes.object,
  onAccountCreate: PropTypes.func,
  body: PropTypes.string
};

CheckoutCreateAccount.defaultProps = {
  onAccountCreate: () => {}
};
export default CheckoutCreateAccount;
